import React, { useMemo } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ReferenceLine,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { type ChartConfig, ChartContainer } from '@/components/ui/chart';
import type { TrendBucket } from '@/pages/analytics/types';
import { AXIS_LINE_STYLE, TICK_LINE_STYLE } from '@/pages/analytics/utils/chartAxis';
import { formatChartAxisDate, formatChartTooltipDate } from '@/pages/analytics/utils/chartDate';
import { colorForSeriesIndex } from '@/pages/analytics/utils/chartPalette';
import { formatBucketLabel } from '@/pages/analytics/utils/dateWindow';
import { pivotSeries, scopeRows } from '@/pages/analytics/utils/series';

import { ChartEmptyState } from './ChartStates';
import { DotsLegend } from './DotsLegend';
import type { ChartScope } from './ScopeToggle';

// Both ComplianceTrendPoint and EscalationTrendPoint satisfy this shape
// (they only differ in their domain-specific count fields).
interface GroupedRateTrendPoint {
  date: string;
  group: string;
  group_name: string;
  rate: number;
}

interface RateTrendChartProps {
  data: GroupedRateTrendPoint[];
  scope: ChartScope;
  /** Color used for the single Overall series. By-program series use the
   * shared `colorForGroup` palette. */
  color: string;
  yMax?: number;
  trendBucket?: TrendBucket;
  height?: number;
  /** Tooltip/legend label for the Overall series. */
  seriesLabel: string;
  /** Draws a dashed horizontal reference line at this rate (e.g. 1 for the 100% goal). */
  targetLine?: number;
  /** Dates (as they appear in `data`) of buckets cut by the range; their labels get a `*`. */
  partialDates?: Set<string>;
  /** Colour per group key (page-wide, so a program keeps its colour across charts). */
  colorByKey?: Map<string, string>;
  /** Gradient fill under each line. Off draws stroke-only lines. */
  gradient?: boolean;
}

const NO_PARTIAL_DATES = new Set<string>();

const formatPercentTick = (v: number) => `${Math.round(v * 100)}%`;
const formatTooltipPercent = (v: unknown) =>
  typeof v === 'number' ? `${(v * 100).toFixed(1)}%` : '';

export const RateTrendChart: React.FC<RateTrendChartProps> = ({
  data,
  scope,
  color,
  yMax = 0.3,
  trendBucket = 'day',
  height = 220,
  seriesLabel,
  targetLine,
  partialDates,
  colorByKey,
  gradient = true,
}) => {
  const { data: rows, series } = useMemo(
    () =>
      pivotSeries(
        scopeRows(data, scope),
        p => p.date,
        p => p.rate,
        'omit',
      ),
    [data, scope],
  );

  // Color for the Overall series uses the per-chart `color` prop; per-program
  // series use visible-series order so one chart does not repeat colors.
  const colorBySeriesKey = useMemo(
    () =>
      new Map(
        series.map((s, index) => [
          s.key,
          s.key === 'overall' ? color : (colorByKey?.get(s.key) ?? colorForSeriesIndex(index)),
        ]),
      ),
    [color, colorByKey, series],
  );
  const colorFor = (key: string): string => colorBySeriesKey.get(key) ?? color;

  const config: ChartConfig = useMemo(() => {
    const c: ChartConfig = {};
    series.forEach(s => {
      c[s.key] = {
        label: s.key === 'overall' ? seriesLabel : s.name,
        color: colorBySeriesKey.get(s.key) ?? color,
      };
    });
    return c;
  }, [color, colorBySeriesKey, series, seriesLabel]);

  if (!rows.length) {
    return <ChartEmptyState height={height} />;
  }

  const withPartialMark = (label: string, date: unknown): string =>
    formatBucketLabel(String(date), label, partialDates ?? NO_PARTIAL_DATES);

  // Unique gradient id per (chart, series) so two RateTrendCharts on the same
  // page don't fight over a shared <defs>.
  const gradientId = (key: string): string =>
    `rate-grad-${seriesLabel.replace(/\s+/g, '-').toLowerCase()}-${key}`;

  const ariaLabel =
    scope === 'overall'
      ? `${seriesLabel} area chart, overall trend across ${rows.length} points`
      : `${seriesLabel} area chart, ${series.length} program${series.length === 1 ? '' : 's'} across ${rows.length} points`;

  return (
    <ChartContainer
      config={config}
      className="!aspect-auto w-full"
      style={{ height }}
      role="img"
      aria-label={ariaLabel}
    >
      <AreaChart data={rows} margin={{ top: 10, right: 16, left: 0, bottom: 4 }}>
        {gradient && (
          <defs>
            {series.map(s => {
              const c = colorFor(s.key);
              return (
                <linearGradient key={s.key} id={gradientId(s.key)} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={c} stopOpacity={0.32} />
                  <stop offset="100%" stopColor={c} stopOpacity={0.02} />
                </linearGradient>
              );
            })}
          </defs>
        )}
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis
          dataKey="date"
          tickFormatter={value => withPartialMark(formatChartAxisDate(value, trendBucket), value)}
          tickLine={TICK_LINE_STYLE}
          axisLine={AXIS_LINE_STYLE}
          fontSize={11}
        />
        <YAxis
          domain={[0, yMax]}
          tickFormatter={formatPercentTick}
          tickLine={TICK_LINE_STYLE}
          axisLine={AXIS_LINE_STYLE}
          fontSize={11}
          width={36}
        />
        <Tooltip
          contentStyle={{
            background: 'hsl(var(--background))',
            border: '1px solid hsl(var(--border))',
            borderRadius: 6,
            fontSize: 12,
          }}
          // Lift the tooltip above adjacent chart cards; without a z-index the
          // wrapper paints under the sibling chart in the 2-col grid (AQ-476).
          wrapperStyle={{ zIndex: 20 }}
          formatter={formatTooltipPercent}
          labelFormatter={value =>
            withPartialMark(formatChartTooltipDate(value, trendBucket), value)
          }
          // Highest rate on top, mirroring the vertical order of the lines at
          // the hovered point (itemSorter sorts ascending on the returned key).
          itemSorter={item => -(typeof item.value === 'number' ? item.value : 0)}
        />

        {targetLine !== undefined && (
          <ReferenceLine
            y={targetLine}
            stroke="hsl(var(--muted-foreground))"
            strokeDasharray="4 4"
            ifOverflow="extendDomain"
          />
        )}

        {series.map(s => (
          <Area
            key={s.key}
            type="monotone"
            dataKey={s.key}
            name={s.key === 'overall' ? seriesLabel : s.name}
            stroke={colorFor(s.key)}
            strokeWidth={2}
            fill={gradient ? `url(#${gradientId(s.key)})` : 'transparent'}
            animationDuration={500}
            connectNulls
          />
        ))}

        {/* Same legend behavior as ScoreTrendChart: shown in by-program scope
            (where multiple programs are stacked) — hidden in Overall since
            the single series needs no label. */}
        {scope === 'by_program' && series.length > 0 && (
          <Legend
            content={<DotsLegend />}
            verticalAlign="bottom"
            wrapperStyle={{ paddingTop: 4 }}
          />
        )}
      </AreaChart>
    </ChartContainer>
  );
};
